import React, { useState, useEffect, useCallback, useRef } from "react";
import { cls } from "cls";
import { useReCaptcha, RecaptchaDisclaimer } from "@wss/re-captcha";
import { removeFileFromReview, uploadPhotos, submitReview, uploadVideos } from "./requests";
import { ItemReview } from "./ProductDisplay";
import ImageAndDescription from "~/common/Reviews/components/LeaveAReview/LeaveAReviewButton/ImageAndDescription";
import { homepage } from "~/common/Constants/Constants";
interface Styles {
  formStyles: string;
  modalDivStyles: string;
  ratingLegendStyles: string;
  ratingStyles: string;
  ratingErrorStyles: string;
  reviewHeadingStyles: string;
  reviewSubStyles: string;
  textAreaStyles: string;
  fileHeadingStyles: string;
  fileSubStyles: string;
  footerStyles: string;
  buttonsStyles: string;
  cancelButtonStyles: string;
  submitButtonStyles: string;
  disclaimerStyles: string;
}
export const defaultStyles = {
  formStyles: cls("mt-4", "md:mt-10", "mb-4", "md:mx-4", "pr-0", "md:w-3/4"),
  modalDivStyles: "",
  ratingLegendStyles: cls("text-sm", "mb-1.5"),
  ratingStyles: "",
  ratingErrorStyles: "",
  reviewHeadingStyles: cls("mb-6", "text-sm"),
  reviewSubStyles: cls("my-1", "leading-4"),
  textAreaStyles: cls("mt-2", "flex-1", "h-32", "md:h-22"),
  fileHeadingStyles: cls("text-sm"),
  fileSubStyles: cls("mb-2", "text-xs", "leading-4"),
  footerStyles: cls("mt-6"),
  buttonsStyles: cls("md:flex-row"),
  cancelButtonStyles: cls("md:mb-0"),
  submitButtonStyles: cls("md:ml-4"),
  disclaimerStyles: cls("mt-6")
};
interface Props {
  productId: string;
  cancelForm: () => void;
  setSuccessMessage: (successMessage: boolean) => void;
  setProducts?: React.Dispatch<React.SetStateAction<ReadonlyArray<ItemReview>>>;
  products: ReadonlyArray<ItemReview>;
  isInModal?: boolean;
  image?: string;
  description?: string;
  productLink?: string;
  styles?: Styles;
  displayExtended?: boolean;
  isOpen?: boolean;
}
export interface FileData {
  file?: File;
  caption: string;
  serverFileName?: string;
}
export interface FormValues {
  itemNumber: string;
  rating: number;
  reviewText: string;
  uploadedFiles: File[];
  uploadedFileData: FileData[];
}
interface ErrorProps {
  rating: string;
  reviewText: string;
  uploadedFiles: string[];
  uploadedFileData: string[];
}
export const removeFile = async (index: number, formData: FormValues, setFormData: React.Dispatch<FormValues>) => {
  const newUploadedFiles = [...formData.uploadedFiles];
  const newUploadedFileData = [...formData.uploadedFileData];
  const removedFileData = formData.uploadedFileData[index];
  const removeFileResponse = await removeFileFromReview(formData.itemNumber, removedFileData?.file?.name ?? "", removedFileData?.serverFileName ?? "", removedFileData?.file?.type ?? "");
  if (!(removeFileResponse instanceof Error) && !removeFileResponse.exceptionOccurred) {
    newUploadedFiles.splice(index, 1);
    newUploadedFileData.splice(index, 1);
    setFormData({
      ...formData,
      uploadedFiles: newUploadedFiles,
      uploadedFileData: newUploadedFileData
    });
  }
};
function containsInvalidCharacters(string: string) {
  return string.length > 0 ? /[<>]/.test(string) : false;
}
export function validateUploadedFileData(formData: Readonly<FormValues>, errors: ErrorProps) {
  if (formData.uploadedFileData.length > 0) {
    formData.uploadedFileData.forEach(fileData => {
      if (containsInvalidCharacters(fileData.caption)) {
        errors.uploadedFileData.push("Caption contains invalid characters.");
      } else {
        errors.uploadedFileData.push("");
      }
    });
  }
}
const CustomerReviewForm = ({
  productId,
  cancelForm,
  setSuccessMessage,
  setProducts,
  products,
  isInModal = false,
  image = "",
  description = "",
  productLink = homepage,
  styles = defaultStyles,
  displayExtended,
  isOpen
}: Props) => {
  const emptyFormData = {
    itemNumber: productId,
    rating: 0,
    reviewText: "",
    uploadedFiles: [],
    uploadedFileData: []
  };
  const emptyFormError = {
    rating: "",
    reviewText: "",
    uploadedFiles: [""],
    uploadedFileData: []
  };
  useEffect(() => {
    if (!isOpen) {
      setFormError(emptyFormError);
      setFormData(emptyFormData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);
  const [formData, setFormData] = useState<FormValues>(emptyFormData);
  const [formError, setFormError] = useState<ErrorProps>(emptyFormError);
  const [selectedThumb, setSelectedThumb] = useState<"Yes" | "No" | null>(null);
  const [objectUrls, setObjectUrls] = useState<string[]>([]);
  const [hoveredStar, setHoveredStar] = useState<number | null>(null);
  const [hoveredThumbs, setHoveredThumbs] = useState<string | null>(null);
  const formElement = useRef<HTMLFormElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [reviewResponseError, setReviewResponseError] = useState("");
  const reCaptcha = useReCaptcha();
  const acceptedFileTypes = ["image/jpg", "image/jpeg", "image/png", "video/mpg", "video/mpeg", "video/mov", "video/avi", "video/wmv", "video/mp4", "video/quicktime"];
  const fileSize25MB = 25 * 1024 ** 2;
  const fileSize300MB = 300 * 1024 ** 2;
  const errorStyles = "text-xs text-red-500 my-1 h-4 font-semibold";
  const generalReviewResponseError = "An error occurred while submitting the review.";
  useEffect(() => {
    const urls = formData.uploadedFiles.map(file => {
      const objectUrl = URL.createObjectURL(file);
      return objectUrl;
    });
    setObjectUrls(urls);
    return () => {
      urls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [formData.uploadedFiles]);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    if (name === "rating") {
      setFormData({
        ...formData,
        rating: Number(value)
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  const handleThumbsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const satisfied = event.target.value === "Yes";
    setSelectedThumb(satisfied ? "Yes" : "No");
  };
  const handleFileUploadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    let errors: ErrorProps = structuredClone(emptyFormError);
    if (files.length > 0) {
      files.forEach(file => {
        if (!acceptedFileTypes.map(ft => ft.split("/")[1]).find(ft => ft !== undefined && file.type.endsWith(ft))) {
          errors.uploadedFiles.push(`'${file.type}' is not an accepted file type`);
        } else if (file.type.startsWith("image") && file.size > fileSize25MB) {
          errors.uploadedFiles.push("Image file size should not exceed 25MB");
        } else if (file.type.startsWith("video") && file.size > fileSize300MB) {
          errors.uploadedFiles.push("Video file size should not exceed 300MB");
        } else if (formData.uploadedFiles.find(f => f.name === file.name)) {
          errors.uploadedFiles.push("This file has already been uploaded.");
        }
      });
    }
    setFormError(errors);
    if (errors.rating || errors.uploadedFiles.filter(e => e !== "").length) return;
    let newFileCaptions: FileData[] = [];
    let newFiles: File[] = [];
    setIsUploading(true);
    const photosToUpload = files.filter(f => f.type.includes("image"));
    if (photosToUpload.length) {
      const uploadedPhotos = await uploadPhotos(formData.itemNumber, photosToUpload);
      if (uploadedPhotos instanceof Error) {
        errors.uploadedFiles.push("An error occurred while uploading your photos. Please try again.");
      } else {
        const failedPhotos = uploadedPhotos.filter(p => !p.success);
        if (failedPhotos) {
          errors = {
            ...errors,
            uploadedFiles: [...errors.uploadedFiles, failedPhotos.map(p => `An error occurred uploading the photo '${p.originalFileName}'.`)].flat()
          };
        }
        const newPhotoFiles = files.filter(f => uploadedPhotos.find(p => p.originalFileName === f.name && p.success));
        newFiles = newFiles.concat(newPhotoFiles);
        const newPhotoFileCaptions = uploadedPhotos.map(p => {
          return {
            file: newPhotoFiles.find(f => f.name === p.originalFileName),
            caption: "",
            serverFileName: p.serverFileName
          };
        });
        newFileCaptions = newFileCaptions.concat(newPhotoFileCaptions);
      }
    }
    const videosToUpload = files.filter(f => f.type.includes("video"));
    if (videosToUpload.length) {
      const uploadedVideos = await uploadVideos(formData.itemNumber, videosToUpload);
      if (uploadedVideos instanceof Error) {
        errors.uploadedFiles.push("An error occurred while uploading your videos. Please try again.");
      } else {
        const failedVideos = uploadedVideos.filter(v => !v.success);
        if (failedVideos) {
          errors = {
            ...errors,
            uploadedFiles: [...errors.uploadedFiles, failedVideos.map(v => `An error occurred uploading the video '${v.originalFileName}'.`)].flat()
          };
        }
        const newVideoFiles = files.filter(f => uploadedVideos.find(v => v.originalFileName === f.name && v.success));
        newFiles = newFiles.concat(newVideoFiles);
        const newVideoFileCaptions = uploadedVideos.map(v => {
          return {
            file: newVideoFiles.find(f => f.name === v.originalFileName),
            caption: "",
            serverFileName: v.serverFileName
          };
        });
        newFileCaptions = newFileCaptions.concat(newVideoFileCaptions);
      }
    }
    setIsUploading(false);
    setFormData(prevFormData => ({
      ...prevFormData,
      uploadedFiles: [...prevFormData.uploadedFiles, ...newFiles],
      uploadedFileData: [...prevFormData.uploadedFileData, ...newFileCaptions]
    }));
    setFormError(errors);

    // Reset value for next use, otherwise onChange won't trigger again
    // in the case where a file is removed and subsequently re-uploaded
    e.target.value = "";
  };
  const handleCaptionChange = useCallback((file: File, index: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (index === -1) return;
    const newCaptions = [...formData.uploadedFileData];
    newCaptions[index] = {
      ...newCaptions[index],
      caption: e.target.value
    };
    setFormData({
      ...formData,
      uploadedFileData: newCaptions
    });
  }, [formData]);
  const handleStarMouseOver = (num: number) => {
    setHoveredStar(num);
  };
  const handleStarMouseOut = () => {
    setHoveredStar(null);
  };
  const handleThumbsMouseOver = (str: string) => {
    setHoveredThumbs(str);
  };
  const handleThumbsMouseOut = () => {
    setHoveredThumbs(null);
  };
  const validateUploadedFiles = (formData: Readonly<FormValues>, errors: ErrorProps) => {
    if (formData.uploadedFiles.length > 0) {
      formData.uploadedFiles.forEach(file => {
        if (file.type.startsWith("image") && file.size > fileSize25MB) {
          errors.uploadedFiles.push("Image file size should not exceed 25MB");
        } else if (file.type.startsWith("video") && file.size > fileSize300MB) {
          errors.uploadedFiles.push("Video file size should not exceed 300MB");
        }
      });
    }
  };
  const validateReviewResponse = (reviewResponse: Response | Error) => {
    if (reviewResponse instanceof Error) {
      setReviewResponseError(generalReviewResponseError);
    } else if (reviewResponse.status === 200) {
      setSuccessMessage(true);
      cancelForm();
      setProducts?.(products.map(p => {
        if (p.itemNumber === productId) {
          return {
            ...p,
            status: "Pending"
          };
        }
        return p;
      }));
    } else {
      setReviewResponseError(generalReviewResponseError);
    }
  };
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setReviewResponseError("");
    const errors: ErrorProps = structuredClone(emptyFormError);
    if (!formData.rating) {
      errors.rating = "Product rating is required";
    }
    validateUploadedFiles(formData, errors);
    validateUploadedFileData(formData, errors);
    if (containsInvalidCharacters(formData.reviewText)) {
      errors.reviewText = "Review contains invalid characters.";
    }
    setFormError(errors);
    if (errors.rating || errors.reviewText || errors.uploadedFiles.filter(e => e !== "").length || errors.uploadedFileData.filter(error => error !== "").length) {
      return;
    }
    const reviewForm = formElement.current;
    if (reviewForm) {
      const reCaptchaToken = await reCaptcha.execute();
      if (reCaptchaToken.isError) throw reCaptchaToken.error;
      const reviewResponse = await submitReview(reviewForm, reCaptchaToken.value);
      validateReviewResponse(reviewResponse);
    }
  };
  const {
    formStyles,
    modalDivStyles,
    ratingLegendStyles,
    ratingStyles,
    ratingErrorStyles,
    reviewHeadingStyles,
    reviewSubStyles,
    textAreaStyles,
    fileHeadingStyles,
    fileSubStyles,
    footerStyles,
    buttonsStyles,
    cancelButtonStyles,
    submitButtonStyles,
    disclaimerStyles
  } = styles;
  return <div data-sentry-component="CustomerReviewForm" data-sentry-source-file="CustomerReviewForm.tsx">
      <form onSubmit={handleSubmit} className={formStyles} ref={formElement} encType="multipart/form-data">
        <div className={modalDivStyles}>
          {isInModal && <ImageAndDescription image={image} description={description} productLink={productLink} itemNumber={productId} />}
          <div className={cls("w-full", "flex", "justify-between", "flex-col", "md:flex-row")}>
            <fieldset className={cls("border-0", "p-0", "flex", "flex-col")} aria-required="true">
              <legend className={cls("text-gray-800", "font-semibold", "m-0", "p-0", ratingLegendStyles)}>
                {isInModal ? <>
                    <span>Product Rating</span>{" "}
                    <span className={cls("font-normal", "text-gray-600")}>
                      (required)
                    </span>
                  </> : "Product Rating*"}
              </legend>
              <input type="hidden" name="itemNumber" value={productId} />
              <div className={cls("flex")}>
                {[1, 2, 3, 4, 5].map((num, index) => <label key={num} className={cls("focus-within:outline-1", ratingStyles)} onMouseOver={() => handleStarMouseOver(num)} onFocus={() => handleStarMouseOver(num)} onMouseOut={handleStarMouseOut} onBlur={handleStarMouseOut}>
                    <span className={cls("sr-only")}>
                      {index + 1} star rating
                    </span>
                    <input id={`starRatings${index}`} type="radio" name="rating" value={num} checked={formData.rating === num} onChange={handleChange} className={cls("sr-only", "border-gray-600", "text-sm", "p-3")} />
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" className={cls("mr-4", "cursor-pointer")}>
                      {num <= (hoveredStar || formData.rating) ? <>
                          <path fill="none" d="M0 0h24v24H0z" />
                          <path d="M12 18.26l-7.053 3.948 1.575-7.928L.587 8.792l8.027-.952L12 .5l3.386 7.34 8.027.952-5.935 5.488 1.575 7.928z" fill="#ffd03d" />
                        </> : <>
                          <path fill="none" d="M0 0h24v24H0z" />
                          <path d="M12 18.26l-7.053 3.948 1.575-7.928L.587 8.792l8.027-.952L12 .5l3.386 7.34 8.027.952-5.935 5.488 1.575 7.928L12 18.26zm0-2.292l4.247 2.377-.949-4.773 3.573-3.305-4.833-.573L12 5.275l-2.038 4.42-4.833.572 3.573 3.305-.949 4.773L12 15.968z" fill="#636e5b" />
                        </>}
                    </svg>
                  </label>)}
              </div>
              <p className={`${errorStyles} ${ratingErrorStyles}`}>
                {formError.rating}
              </p>
            </fieldset>
            <fieldset className={cls("border-0", "flex", "flex-col", "p-0", "mb-6")}>
              <legend className={cls("text-gray-800", "text-sm", "font-semibold")}>
                Did this product satisfy your needs?
              </legend>
              <div className={cls("flex")}>
                <label htmlFor="thumbsUp" className="m-0" onMouseOver={() => handleThumbsMouseOver("Yes")} onFocus={() => handleThumbsMouseOver("Yes")} onMouseOut={handleThumbsMouseOut} onBlur={handleThumbsMouseOut}>
                  <div className={cls("relative")}>
                    <input type="radio" id="thumbsUp" name="productSatisfiedNeeds" value="Yes" checked={selectedThumb === "Yes"} onChange={handleThumbsChange} className={cls("sr-only")} />
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="#298200" aria-hidden="true" onClick={() => setSelectedThumb("Yes")} className={cls("absolute", "cursor-pointer")} style={{
                    top: "3px"
                  }} data-sentry-element="svg" data-sentry-source-file="CustomerReviewForm.tsx">
                      {selectedThumb === "Yes" || hoveredThumbs === "Yes" ? <>
                          <path fill="none" d="M0 0h24v24H0z" />
                          <path d="M2 9h3v12H2a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1zm5.293-1.293l6.4-6.4a.5.5 0 0 1 .654-.047l.853.64a1.5 1.5 0 0 1 .553 1.57L14.6 8H21a2 2 0 0 1 2 2v2.104a2 2 0 0 1-.15.762l-3.095 7.515a1 1 0 0 1-.925.619H8a1 1 0 0 1-1-1V8.414a1 1 0 0 1 .293-.707z" />{" "}
                        </> : <>
                          <path fill="none" d="M0 0h24v24H0z" />
                          <path d="M14.6 8H21a2 2 0 0 1 2 2v2.104a2 2 0 0 1-.15.762l-3.095 7.515a1 1 0 0 1-.925.619H2a1 1 0 0 1-1-1V10a1 1 0 0 1 1-1h3.482a1 1 0 0 0 .817-.423L11.752.85a.5.5 0 0 1 .632-.159l1.814.907a2.5 2.5 0 0 1 1.305 2.853L14.6 8zM7 10.588V19h11.16L21 12.104V10h-6.4a2 2 0 0 1-1.938-2.493l.903-3.548a.5.5 0 0 0-.261-.571l-.661-.33-4.71 6.672c-.25.354-.57.644-.933.858zM5 11H3v8h2v-8z" />
                        </>}
                    </svg>
                    <span className={cls("text-sm", "m-6", "mr-4", "font-normal")}>
                      Yes
                    </span>
                  </div>
                </label>
                <label htmlFor="thumbsDown" className="m-0" onMouseOver={() => handleThumbsMouseOver("No")} onFocus={() => handleThumbsMouseOver("No")} onMouseOut={handleThumbsMouseOut} onBlur={handleThumbsMouseOut}>
                  <div className={cls("relative")}>
                    <input type="radio" id="thumbsDown" name="productSatisfiedNeeds" value="No" checked={selectedThumb === "No"} onChange={handleThumbsChange} className={cls("sr-only")} />
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="#298200" aria-hidden="true" onClick={() => setSelectedThumb("No")} className={cls("absolute", "cursor-pointer")} style={{
                    top: "3px"
                  }} data-sentry-element="svg" data-sentry-source-file="CustomerReviewForm.tsx">
                      {selectedThumb === "No" || hoveredThumbs === "No" ? <>
                          <path fill="none" d="M0 0h24v24H0z" />
                          <path d="M22 15h-3V3h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1zm-5.293 1.293l-6.4 6.4a.5.5 0 0 1-.654.047L8.8 22.1a1.5 1.5 0 0 1-.553-1.57L9.4 16H3a2 2 0 0 1-2-2v-2.104a2 2 0 0 1 .15-.762L4.246 3.62A1 1 0 0 1 5.17 3H16a1 1 0 0 1 1 1v11.586a1 1 0 0 1-.293.707z" />
                        </> : <>
                          <path fill="none" d="M0 0h24v24H0z" />
                          <path d="M9.4 16H3a2 2 0 0 1-2-2v-2.104a2 2 0 0 1 .15-.762L4.246 3.62A1 1 0 0 1 5.17 3H22a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1h-3.482a1 1 0 0 0-.817.423l-5.453 7.726a.5.5 0 0 1-.632.159L9.802 22.4a2.5 2.5 0 0 1-1.305-2.853L9.4 16zm7.6-2.588V5H5.84L3 11.896V14h6.4a2 2 0 0 1 1.938 2.493l-.903 3.548a.5.5 0 0 0 .261.571l.661.33 4.71-6.672c.25-.354.57-.644.933-.858zM19 13h2V5h-2v8z" />
                        </>}
                    </svg>
                    <span className={cls("text-sm", "ml-6", "font-normal")}>
                      No
                    </span>
                  </div>
                </label>
              </div>
            </fieldset>
          </div>
          <label htmlFor="customerReview" className={cls("text-gray-800", "font-semibold", reviewHeadingStyles)}>
            Add a written review
            <p className={cls("text-gray-600", "text-sm", "font-normal", reviewSubStyles)} id="customerReviewHelperText">
              What did you like or dislike/ What did you use this product for?
            </p>
            <div className={cls("flex", "w-full")}>
              <textarea id="customerReview" name="reviewText" aria-describedby="customerReviewHelperText" value={formData.reviewText} onChange={handleChange} className={cls("font-sans", "text-sm", "p-3", "mb-0", "border-gray-600", textAreaStyles)} />
            </div>
            <p className={errorStyles} data-testid="review-text-error">
              {formError.reviewText}
            </p>
          </label>
          <p className={cls("text-gray-800", "font-semibold", "mb-1", fileHeadingStyles)}>
            Add a photo or video
          </p>
          <p className={cls("text-gray-600", "font-normal", fileSubStyles)}>
            Accepted file types: .jpg, .png, .mpg, mpeg, mov, avi, wmv, mp4 (max
            file size: 25MB for photos and 300MB for videos)
          </p>
          <label htmlFor="imageUpload" style={{
          width: "15px",
          height: "15px"
        }} className={cls("bg-gray-50", "hover:bg-gray-200", "border-gray-600", "hover:border-gray-700", "rounded", "border-dashed", "border", "cursor-pointer", "duration-100", "ease-in", "p-6", "m-0", "relative", "focus:outline")}>
            <span className="sr-only">
              Add a photo or video. Accepted file types: .jpg, .png, .mpg, mpeg,
              mov, avi, wmv, mp4 (max file size: 25MB for photos and 300MB for
              videos).
            </span>
            <input type="file" id="imageUpload" name="imageUpload" multiple accept={acceptedFileTypes.join(", ")} onChange={handleFileUploadChange} style={{
            width: "15px",
            height: "15px",
            top: "-25px",
            left: "-25px"
          }} className={cls("bg-gray-50", "hover:bg-gray-200", "border-gray-600", "hover:border-gray-700", "rounded", "border-dashed", "border", "cursor-pointer", "duration-100", "ease-in", displayExtended ? "p-8" : "p-6", "m-0", "absolute", "focus:outline", "text-transparent", "relative", "file:hidden")} />
            {isUploading ? <>
                <span className="icon-spinner absolute" style={{
              top: "25px",
              left: "25px"
            }} />
              </> : <>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" aria-hidden="true" className="absolute focus:ring" style={{
              top: "19px",
              left: "19px"
            }}>
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path d="M11 11V5h2v6h6v2h-6v6h-2v-6H5v-2z" fill="#298200" />
                </svg>
              </>}
          </label>
          {formData.uploadedFiles.map((file, index) => {
          if (file.type.startsWith("image")) {
            return <div key={formData.uploadedFileData[index]?.serverFileName} className={cls("inline-block", "flex", "mt-6")}>
                  <input type="hidden" name={`photoFile${index + 1}`} value={formData.uploadedFileData[index]?.serverFileName} />
                  <div>
                    <img src={objectUrls[index]} alt={file.name} key={formData.uploadedFileData[index]?.serverFileName} className={cls("rounded")} style={{
                  width: "65px",
                  height: "65px"
                }} />
                    <button type="button" className={cls("cursor-pointer", "border-none", "bg-white", "text-green-500", "underline", "text-sm")} onClick={() => removeFile(index, formData, setFormData)}>
                      Remove
                    </button>
                  </div>
                  <div className={cls("w-full", "-ml-9")}>
                    <label className={cls("block", "text-gray-900", "text-sm", "mb-1")}>
                      Media Caption
                    </label>
                    <input type="text" name={`photoCaption${index + 1}`} value={formData.uploadedFileData[index]?.caption || ""} onChange={handleCaptionChange(file, index)} className={cls("p-2", "rounded")} />
                    <p className={errorStyles} data-testid="upload-photo-error">
                      {formError.uploadedFileData[index]}
                    </p>
                  </div>
                </div>;
          }
          if (file.type.startsWith("video")) {
            return <div key={formData.uploadedFileData[index]?.serverFileName} className={cls("inline-block", "flex", "mt-8")}>
                  <input name={`videoFile${index + 1}`} type="hidden" value={formData.uploadedFileData[index]?.serverFileName} />
                  <div>
                    <video src={objectUrls[index]} controls width="100" height="100" key={formData.uploadedFileData[index]?.serverFileName} className={cls("rounded")} style={{
                  width: "65px",
                  height: "65px"
                }} />
                    <button type="button" className={cls("cursor-pointer", "border-none", "bg-white", "text-green-500", "underline", "text-sm")} onClick={() => removeFile(index, formData, setFormData)}>
                      Remove
                    </button>
                  </div>
                  <div className={cls("w-full", "-ml-9")}>
                    <label className={cls("block", "text-gray-900", "text-sm", "mb-1")}>
                      Media Caption
                    </label>
                    <input type="text" name={`videoCaption${index + 1}`} value={formData.uploadedFileData[index]?.caption || ""} onChange={handleCaptionChange(file, index)} className={cls("p-2", "rounded")} />
                    <p className={errorStyles} data-testid="upload-video-error">
                      {formError.uploadedFileData[index]}
                    </p>
                  </div>
                </div>;
          }
          return null;
        })}
          {formError.uploadedFiles && !formError.uploadedFiles.every(e => e === "") && <p className={cls("text-xs", "text-red-500", "m-0")}>
                {formError.uploadedFiles.filter(e => e !== "").map(e => <>
                      {e}
                      <br />
                    </>)}
              </p>}
          <p className={errorStyles} data-testid="review-response-error">
            {reviewResponseError}
          </p>
          {isInModal && <br />}
        </div>
        <div className={cls("xl:flex", footerStyles)}>
          <div className={cls("flex", "flex-col", buttonsStyles)}>
            <button type="button" onClick={() => {
            formElement.current?.reset();
            setFormError(emptyFormError);
            setFormData(emptyFormData);
            cancelForm();
          }} className={cls("mb-2", "hover:bg-position-y-15", "rounded-normal", "text-base", "leading-3", "py-3", "px-6", "bg-transparent", "hover:bg-gray-200", "border-gray-600", "text-gray-900", "align-middle", "font-semibold", "btn", "btn-light", cancelButtonStyles)}>
              Cancel
            </button>
            <button type="submit" className={cls("btn", "btn-primary", "bg-origin-box-border", "bg-repeat-x", "border-solid", "border", "box-border", "inline-block", "text-center", "antialiased", "hover:bg-position-y-15", "rounded-normal", "text-base", "leading-3", "py-3", "px-6", "text-white", "hover:text-white", "align-middle", "font-semibold", submitButtonStyles)}>
              Submit Review
            </button>
          </div>
          <RecaptchaDisclaimer className={cls("text-xs", "text-gray-700", "leading-4", "xl:ml-6", disclaimerStyles)} data-sentry-element="RecaptchaDisclaimer" data-sentry-source-file="CustomerReviewForm.tsx" />
        </div>
      </form>
    </div>;
};
export default CustomerReviewForm;